import React, { useMemo, useState } from 'react';
import { StepInfo, Wizard } from 'shared/components/Wizard';
import { ClosingTaskWizardLayout } from 'sellDirect/components/closingDash/ClosingTaskWizardLayout';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { Box, Text } from '@opendoor/bricks/core';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { ServiceProvidersForm } from 'sellDirect/components/closingDash/serviceProviders/ServiceProvidersForm';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import {
  ClosingFAQs,
  getServiceProvidersFaqs,
  getServiceProvidersAgentFaqs,
} from 'sellDirect/components/closingDash/ClosingFAQs';
import { useHistory } from 'platform/HistoryProvider';
import { generatePath } from 'react-router-dom';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

export interface ServiceProvidersWizardProps {
  offer: UnderContractOffer;
  closingTasks: ClosingTasks;
}

/**
 * Wizard to allow users to update service providers information
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const ServiceProvidersWizard: React.FC<ServiceProvidersWizardProps> = ({
  offer,
  closingTasks,
}) => {
  const { addressUUID } = useQualificationContext();
  const { history, goBack } = useHistory();
  const initialLocation = useMemo(() => history.location, [history]);
  const [inEditMode, setInEditMode] = useState(false);
  const faqs = offer.shouldSeeAgentExperience
    ? getServiceProvidersAgentFaqs()
    : getServiceProvidersFaqs();
  const steps: StepInfo[] = [
    {
      title: 'Service providers',
      // eslint-disable-next-line react/display-name
      content: () => (
        <ClosingTaskWizardLayout
          rightComponent={
            <ClosingFAQs faqs={faqs} hideTitle data-testid="service-providers-closing-faqs" />
          }
        >
          <Box mt={9}>
            <Text mb={9}>
              Share a few details to ensure a smooth transition. This won’t affect{' '}
              {offer.shouldSeeAgentExperience ? 'the' : 'your'} sale price.
            </Text>
            <ServiceProvidersForm
              shouldSeeAgentExperience={offer.shouldSeeAgentExperience}
              closingTasks={closingTasks}
              setInEditMode={setInEditMode}
              onCompleted={() => {
                history.push(generatePath(getHubSellingRoute(), { addressUUID }));
                showToast(
                  'info',
                  'Successfully submitted service provider info.',
                  'cake-service-providers-submitted-info-toast',
                );
              }}
            />
          </Box>
        </ClosingTaskWizardLayout>
      ),
      confirmOnClose: inEditMode
        ? {
            cancelText: 'Finish answering questions',
            confirmText: 'Close without saving',
            content: (
              <Text mb="8">
                If you leave before you finish answering all questions, it won’t save your progress.
              </Text>
            ),
            title: 'Are you sure you want to stop?',
          }
        : undefined,
    },
  ];

  return (
    <Wizard
      steps={steps}
      title="Service Providers"
      onClose={() =>
        goBack({
          before: initialLocation,
          fallbackLocation: getHubSellingRoute(),
          addressUUID,
        })
      }
    />
  );
};
